import type { ServiceArea } from "../serviceAreas";

export const nyAreas: ServiceArea[] = [
  {
    slug: "new-york-city-ny",
    city: "Manhattan",
    state: "NY",
    county: "New York County",
    title: "Manhattan Limo Service & NYC Airport Car Service",
    description:
      "Chauffeured black car and limo service in Manhattan, NYC. Pre-booked JFK, LaGuardia and Newark transfers, cruise and event rides. Call (929) 452-6031.",
    h1: "Limo Service in Manhattan, NYC",
    tagline:
      "Pre-booked Manhattan pickups for airport runs, cruise days and evenings out, driven by a New Jersey chauffeur team.",
    intro: [
      "Most of our Manhattan work starts in New Jersey: a client heading from South Jersey to a Midtown meeting, a family catching a sailing from the West Side piers, or a group going into the city for a Broadway show and dinner. We also run scheduled pickups from Manhattan hotels and apartments to JFK, LaGuardia and Newark, booked in advance so the vehicle and chauffeur are set before the day.",
      "Our chauffeurs reach the island through the Lincoln Tunnel, the Holland Tunnel or the George Washington Bridge, choosing the crossing by time of day and where you are going. Midtown curb space is tight, so we agree on an exact pickup spot, such as a hotel entrance or a side-street corner, and confirm tolls and any parking in your quote.",
    ],
    airports: [
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 25–45 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 35–60 min" },
      { code: "JFK", label: "JFK International", time: "About 45–75 min" },
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 1 hr 45 min–2 hr 15 min" },
    ],
    landmarks: [
      "Times Square and the Midtown hotel district",
      "Penn Station and Madison Square Garden",
      "Grand Central Terminal",
      "Manhattan Cruise Terminal on the West Side",
      "Hudson Yards and the Javits Center",
      "Lincoln Center",
    ],
    neighborhoods: [
      "Midtown",
      "Upper East Side",
      "Upper West Side",
      "Chelsea",
      "Tribeca",
      "Financial District",
    ],
    popular: [
      {
        label: "NYC airport transfers",
        path: "/airports",
        blurb:
          "Hotel and apartment pickups for JFK, LaGuardia and Newark, timed around your terminal and the crosstown traffic.",
      },
      {
        label: "Cruise terminal rides",
        path: "/services/cruise-port",
        blurb:
          "Drop-offs and pickups at the West Side piers, with room for luggage and a meeting point agreed for disembarkation day.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Rides between New Jersey offices and Midtown or Financial District meetings, with a chauffeur waiting between appointments.",
      },
      {
        label: "Night on the town",
        path: "/services/night-on-the-town",
        blurb:
          "Theater, dinner and late reservations without hunting for a garage, plus a confirmed ride back home.",
      },
    ],
    faqs: [
      {
        q: "Do you have an office in Manhattan?",
        a: "No. GS Limo Elite is based in Glassboro, New Jersey, and serves Manhattan with pre-booked trips. Reserving ahead lets us schedule the chauffeur and vehicle around your pickup time.",
      },
      {
        q: "Which crossing do you use from New Jersey?",
        a: "It depends on your destination and the hour. The Lincoln Tunnel usually suits Midtown, the Holland Tunnel suits downtown, and the George Washington Bridge suits the Upper West Side. Tolls and any Manhattan zone charges are confirmed in your quote.",
      },
      {
        q: "Should I fly out of JFK, LaGuardia or Newark from Manhattan?",
        a: "LaGuardia is often the shortest drive from Midtown or the East Side, while Newark can be quicker from the West Side or downtown. We plan the pickup around whichever airport your ticket uses.",
      },
      {
        q: "Can you handle a group heading to a Midtown show?",
        a: "Yes. Sedans and SUVs suit small parties, and a Mercedes Sprinter works well for larger groups. Tell us your headcount and show time and we will suggest a vehicle and a drop-off point near the theater.",
      },
    ],
    nearby: ["brooklyn-ny", "queens-ny", "hoboken-nj", "jersey-city-nj"],
  },
  {
    slug: "brooklyn-ny",
    city: "Brooklyn",
    state: "NY",
    county: "Kings County",
    title: "Brooklyn Limo & Black Car Service to JFK and EWR",
    description:
      "Pre-booked limo and black car service in Brooklyn, NY. JFK, LaGuardia and Newark transfers, Red Hook cruise and Barclays Center rides. Call (929) 452-6031.",
    h1: "Limo Service in Brooklyn, NY",
    tagline:
      "Scheduled Brooklyn pickups for flights, sailings from Red Hook and nights at Barclays Center.",
    intro: [
      "Brooklyn trips make up a steady share of our New York schedule. We drive families from Park Slope and Bay Ridge to JFK, bring wedding parties from New Jersey to waterfront venues in DUMBO and Williamsburg, and carry cruise passengers with a week of luggage to the terminal at Red Hook.",
      "Coming from New Jersey, our chauffeurs cross Staten Island and the Verrazzano-Narrows Bridge, or take the Holland Tunnel and the Hugh L. Carey Tunnel, depending on traffic. Inside the borough we plan around the Belt Parkway and the BQE, and every booking is confirmed in writing before the trip.",
    ],
    airports: [
      { code: "JFK", label: "JFK International", time: "About 35–60 min" },
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 30–50 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 40–65 min" },
    ],
    landmarks: [
      "Barclays Center",
      "Brooklyn Cruise Terminal in Red Hook",
      "Brooklyn Bridge Park and DUMBO",
      "Downtown Brooklyn hotels",
      "Kings Theatre in Flatbush",
      "Coney Island boardwalk",
    ],
    neighborhoods: [
      "Brooklyn Heights",
      "Park Slope",
      "Williamsburg",
      "Bay Ridge",
      "Carroll Gardens",
      "DUMBO",
    ],
    popular: [
      {
        label: "Cruise port transfers",
        path: "/services/cruise-port",
        blurb:
          "Rides to and from the Red Hook terminal, with luggage space planned for the whole party.",
      },
      {
        label: "JFK car service",
        path: "/services/airport-transportation/jfk",
        blurb:
          "Belt Parkway runs to JFK from across the borough, scheduled with a buffer for peak-hour traffic.",
      },
      {
        label: "Concerts and arena events",
        path: "/services/concerts",
        blurb:
          "Drop-off and an agreed pickup spot for shows at Barclays Center and Kings Theatre.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Couple and guest transportation for Brooklyn waterfront and loft venues, built around the day's schedule.",
      },
    ],
    faqs: [
      {
        q: "Do you take passengers to the Brooklyn Cruise Terminal?",
        a: "Yes. We schedule drop-offs at the Red Hook terminal before your boarding window and can return for you on disembarkation day. Let us know how many bags you are bringing so we send the right vehicle.",
      },
      {
        q: "Which bridge or tunnel do you use to reach Brooklyn from New Jersey?",
        a: "Usually the Verrazzano-Narrows Bridge by way of Staten Island, or the Holland Tunnel followed by the Hugh L. Carey Tunnel. Crossing tolls are listed in the quote before you book.",
      },
      {
        q: "Is JFK or Newark better from Brooklyn?",
        a: "JFK is normally the closer airport for most of Brooklyn. Newark can make sense from western neighborhoods when the fare or flight time is better, and we will time either trip accordingly.",
      },
      {
        q: "Can you pick us up after an event at Barclays Center?",
        a: "Yes. Streets around the arena get crowded after shows, so we confirm a pickup corner nearby in advance and keep in touch by phone as the event lets out.",
      },
    ],
    nearby: ["new-york-city-ny", "queens-ny", "staten-island-ny", "jersey-city-nj"],
  },
  {
    slug: "queens-ny",
    city: "Queens",
    state: "NY",
    county: "Queens County",
    title: "Queens Car Service for JFK, LaGuardia & Citi Field",
    description:
      "Chauffeured car service in Queens, NY, home to JFK and LaGuardia. Airport transfers, Citi Field games, US Open days and Newark runs. Call (929) 452-6031.",
    h1: "Limo Service in Queens, NY",
    tagline:
      "Queens is where both New York airports sit, and we schedule pickups across the borough to match.",
    intro: [
      "With JFK in the south of the borough and LaGuardia on Flushing Bay, Queens sees more of our chauffeurs than any other part of New York. Typical bookings include arrivals collected at the terminal and driven to New Jersey, Long Island City hotel guests heading to Newark, and groups going to a Mets game at Citi Field or a session of the US Open in Flushing Meadows.",
      "Our routes here rely on the Van Wyck Expressway, the Grand Central Parkway and the Long Island Expressway, which can all slow sharply at rush hour. We track inbound flights, plan the timing before the day and confirm the pickup details with you, since we run scheduled trips from New Jersey rather than street hails.",
    ],
    airports: [
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 15–30 min" },
      { code: "JFK", label: "JFK International", time: "About 20–40 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 50–80 min" },
    ],
    landmarks: [
      "Citi Field",
      "USTA Billie Jean King National Tennis Center",
      "Flushing Meadows Corona Park",
      "Long Island City waterfront hotels",
      "Forest Hills Stadium",
      "Jamaica Station and the JFK AirTrain",
    ],
    neighborhoods: [
      "Astoria",
      "Long Island City",
      "Flushing",
      "Forest Hills",
      "Bayside",
      "Jamaica",
    ],
    popular: [
      {
        label: "LaGuardia car service",
        path: "/services/airport-transportation/lga",
        blurb:
          "Short hops to LaGuardia terminals from Astoria, Flushing and nearby neighborhoods.",
      },
      {
        label: "JFK arrivals and departures",
        path: "/services/airport-transportation/jfk",
        blurb:
          "Flight-tracked pickups at JFK, with a chauffeur ready when you clear baggage claim.",
      },
      {
        label: "Sports events",
        path: "/services/sports-events",
        blurb:
          "Rides to Citi Field and the US Open grounds, with a return pickup point set before the first pitch or first serve.",
      },
      {
        label: "Hourly and as-directed",
        path: "/services/hourly-as-directed",
        blurb:
          "A vehicle on standby for several stops across Queens and the city, billed by the hour.",
      },
    ],
    faqs: [
      {
        q: "Can you pick me up at JFK and drive me to New Jersey?",
        a: "Yes. That is one of our most common New York trips. We monitor your flight, adjust for delays and take you to your door anywhere in New Jersey, with tolls confirmed in the quote.",
      },
      {
        q: "How do airport pickups work at JFK and LaGuardia?",
        a: "We confirm a meeting point before the trip, either at the terminal arrivals curb or inside baggage claim. Airport parking or waiting charges, if any, are spelled out in your quote.",
      },
      {
        q: "Do you drive groups to Mets games or the US Open?",
        a: "Yes. A Sprinter van is a good fit for larger groups heading to Citi Field or the tennis center. Traffic builds quickly near Flushing Meadows on event days, so we leave extra time.",
      },
      {
        q: "Do you serve all of Queens?",
        a: "We take pre-booked pickups throughout the borough, from Astoria and Long Island City to Bayside and the Rockaways. Share your address when you request a quote.",
      },
    ],
    nearby: ["brooklyn-ny", "long-island-ny", "new-york-city-ny", "bronx-ny"],
  },
  {
    slug: "staten-island-ny",
    city: "Staten Island",
    state: "NY",
    county: "Richmond County",
    title: "Staten Island Limo Service & Newark Airport Rides",
    description:
      "Limo and car service in Staten Island, NY. Fast Newark Airport transfers plus JFK and LaGuardia runs, cruise departures and wedding rides. Call (929) 452-6031.",
    h1: "Limo Service in Staten Island, NY",
    tagline:
      "The New York borough closest to New Jersey, linked to us by three bridges and a short drive to Newark.",
    intro: [
      "Staten Island sits right across the Arthur Kill from New Jersey, so it is the easiest New York borough for our chauffeurs to reach. The Goethals Bridge, the Outerbridge Crossing and the Bayonne Bridge all connect it to New Jersey, which makes Newark Liberty a short ride from most of the island.",
      "Residents book us for Newark flights, longer runs to JFK over the Verrazzano-Narrows Bridge, proms and weddings, and cruise days at Cape Liberty in Bayonne, just over the Bayonne Bridge. We confirm the route and your pickup on the Staten Island Expressway or West Shore Expressway side before you travel.",
    ],
    airports: [
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 20–35 min" },
      { code: "JFK", label: "JFK International", time: "About 45–70 min" },
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 50–75 min" },
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 1 hr 15 min–1 hr 45 min" },
    ],
    landmarks: [
      "St. George Ferry Terminal",
      "Empire Outlets in St. George",
      "Snug Harbor Cultural Center and Botanical Garden",
      "Staten Island Mall",
      "Historic Richmond Town",
      "Wagner College on Grymes Hill",
    ],
    neighborhoods: [
      "St. George",
      "Tottenville",
      "Great Kills",
      "New Dorp",
      "Todt Hill",
      "Huguenot",
    ],
    popular: [
      {
        label: "Newark Airport car service",
        path: "/services/airport-transportation/ewr",
        blurb:
          "Quick crossings to EWR over the Goethals Bridge or the Outerbridge, timed to your departure.",
      },
      {
        label: "Cruise port rides",
        path: "/services/cruise-port",
        blurb:
          "Transfers to Cape Liberty in Bayonne and the Manhattan and Brooklyn terminals, with space for luggage.",
      },
      {
        label: "Prom transportation",
        path: "/services/prom",
        blurb:
          "Group rides for Staten Island high school proms, with pickup and return times agreed with parents.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Transportation for ceremonies and receptions on the island or across the bridges in New Jersey.",
      },
    ],
    faqs: [
      {
        q: "Is Newark the closest airport to Staten Island?",
        a: "For most of the island, yes. Newark Liberty is usually a short drive over the Goethals Bridge, while JFK and LaGuardia require crossing the Verrazzano-Narrows Bridge and Brooklyn or Queens traffic.",
      },
      {
        q: "Do you charge for the bridge tolls?",
        a: "Any tolls on your route, including the Verrazzano-Narrows Bridge, are listed in your quote, so you know the full cost before you confirm.",
      },
      {
        q: "Can you take us to a cruise at Cape Liberty?",
        a: "Yes. Cape Liberty in Bayonne is close to Staten Island by way of the Bayonne Bridge. We also drive to the Manhattan and Brooklyn cruise terminals.",
      },
      {
        q: "How many people can ride together?",
        a: "Executive sedans and SUVs suit couples and small families, Mercedes Sprinter vans carry larger parties, and party bus requests can be quoted for bigger groups. Share your headcount and luggage and we will match the vehicle.",
      },
    ],
    nearby: ["brooklyn-ny", "new-york-city-ny", "jersey-city-nj", "newark-nj"],
  },
  {
    slug: "bronx-ny",
    city: "Bronx",
    state: "NY",
    county: "Bronx County",
    title: "Bronx Limo Service for Yankee Stadium & LaGuardia",
    description:
      "Chauffeured limo and car service in the Bronx, NY. LaGuardia, JFK and Newark airport transfers, Yankee Stadium games and family events. Call (929) 452-6031.",
    h1: "Limo Service in the Bronx, NY",
    tagline:
      "From Riverdale to City Island, we run scheduled Bronx pickups for games, flights and celebrations.",
    intro: [
      "Our Bronx bookings often revolve around Yankee Stadium, whether it is a New Jersey group heading to a game or a family planning a birthday around a Yankees night. We also drive guests to the Bronx Zoo and the New York Botanical Garden, students and parents to Fordham University, and diners to Arthur Avenue in Belmont.",
      "From New Jersey, the George Washington Bridge leads straight onto the Cross Bronx Expressway, and the Major Deegan and Bruckner expressways cover the rest of the borough. Those roads are among the busiest in the region, so we schedule with a cushion and agree on every pickup and return time before the trip.",
    ],
    airports: [
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 20–35 min" },
      { code: "JFK", label: "JFK International", time: "About 35–55 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 40–65 min" },
    ],
    landmarks: [
      "Yankee Stadium",
      "Bronx Zoo",
      "New York Botanical Garden",
      "Arthur Avenue in Belmont",
      "Fordham University Rose Hill campus",
      "Orchard Beach and Pelham Bay Park",
    ],
    neighborhoods: [
      "Riverdale",
      "Fordham",
      "Pelham Bay",
      "City Island",
      "Throgs Neck",
      "Mott Haven",
    ],
    popular: [
      {
        label: "Yankee Stadium rides",
        path: "/services/sports-events",
        blurb:
          "Game-day drop-off near the stadium and a pickup point set before the final out.",
      },
      {
        label: "LaGuardia car service",
        path: "/services/airport-transportation/lga",
        blurb:
          "Runs across the Whitestone or RFK bridges to LaGuardia, planned around your terminal.",
      },
      {
        label: "Birthday celebrations",
        path: "/services/birthdays",
        blurb:
          "A chauffeured vehicle for dinner on Arthur Avenue or a night out, with the ride home already arranged.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Rides for meetings, hospital visits and campus appointments, including trips to and from New Jersey.",
      },
    ],
    faqs: [
      {
        q: "Can you drive our group from New Jersey to a Yankees game?",
        a: "Yes. We pick up in New Jersey, cross the George Washington Bridge and drop you near the stadium. Post-game traffic is heavy, so we agree on a meeting spot a short walk away.",
      },
      {
        q: "Which airport is closest to the Bronx?",
        a: "LaGuardia is usually the nearest, reached over the Whitestone Bridge or the RFK Bridge. JFK and Newark are also routine, and the tolls for each route are confirmed in your quote.",
      },
      {
        q: "Do you serve Riverdale and City Island?",
        a: "Yes. We take pre-booked pickups across the borough, including Riverdale, City Island, Throgs Neck and the neighborhoods around Fordham.",
      },
      {
        q: "Can we book a vehicle for a few hours in the Bronx?",
        a: "Yes. Hourly service works well for a zoo visit followed by dinner, or a day split between several family stops.",
      },
    ],
    nearby: ["new-york-city-ny", "queens-ny", "white-plains-ny", "hoboken-nj"],
  },
  {
    slug: "long-island-ny",
    city: "Long Island",
    state: "NY",
    county: "Nassau County",
    title: "Long Island Limo Service in Nassau County",
    description:
      "Limo and black car service on Long Island, serving Nassau County towns with JFK, LaGuardia and Newark transfers, UBS Arena and event rides. Call (929) 452-6031.",
    h1: "Limo Service on Long Island, NY",
    tagline:
      "Nassau County pickups for airport transfers, arena nights and trips back to New Jersey.",
    intro: [
      "On Long Island we concentrate on Nassau County, taking pre-booked trips from Garden City, Mineola, Hempstead, Great Neck, Manhasset and Long Beach. Common requests include JFK and LaGuardia transfers, concerts and hockey at UBS Arena at Belmont Park, Hofstra University events, and longer rides to family or business in New Jersey.",
      "Nassau travel runs along the Northern State and Southern State parkways, the Meadowbrook Parkway and the Long Island Expressway, with the Cross Island Parkway linking to the airports and the Throgs Neck Bridge. Because commercial vehicles are barred from many Long Island parkways, our chauffeurs plan legal routes for vans in advance and confirm them in the quote.",
    ],
    airports: [
      { code: "JFK", label: "JFK International", time: "About 20–40 min" },
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 25–45 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 60–95 min" },
    ],
    landmarks: [
      "UBS Arena at Belmont Park",
      "Roosevelt Field mall",
      "Nassau Veterans Memorial Coliseum",
      "Hofstra University",
      "Jones Beach State Park",
      "Mineola LIRR station",
    ],
    neighborhoods: [
      "Garden City",
      "Hempstead",
      "Great Neck",
      "Mineola",
      "Long Beach",
      "Manhasset",
    ],
    popular: [
      {
        label: "JFK transfers",
        path: "/services/airport-transportation/jfk",
        blurb:
          "Door-to-terminal rides from Nassau towns to JFK, timed around Belt Parkway and Cross Island traffic.",
      },
      {
        label: "Concerts at UBS Arena",
        path: "/services/concerts",
        blurb:
          "Drop-off at Belmont Park and a planned pickup point when the show ends.",
      },
      {
        label: "Bachelorette parties",
        path: "/services/bachelorette-party",
        blurb:
          "A Sprinter or party bus request for a night of stops, from Long Beach to the city.",
      },
      {
        label: "Wedding transportation",
        path: "/services/weddings",
        blurb:
          "Guest shuttles and couple transport for Gold Coast estates and North Shore venues.",
      },
    ],
    faqs: [
      {
        q: "Which parts of Long Island do you cover?",
        a: "We mainly serve Nassau County, including Garden City, Hempstead, Mineola, Great Neck, Manhasset and Long Beach. Trips farther east can be quoted on request.",
      },
      {
        q: "Can a Sprinter van use the Long Island parkways?",
        a: "Commercial vehicles are restricted on many Long Island parkways, so Sprinter trips use the Long Island Expressway and other permitted roads. We plan that route ahead so timing stays accurate.",
      },
      {
        q: "Is JFK or LaGuardia better from Nassau County?",
        a: "JFK is usually closer for the South Shore and central Nassau, while LaGuardia can be convenient from Great Neck and the North Shore. Either way, we schedule the pickup around your flight.",
      },
      {
        q: "Do you drive from Long Island to New Jersey?",
        a: "Yes. Long Island to New Jersey runs are routine, usually crossing via the Verrazzano-Narrows Bridge or through Manhattan. Bridge and tunnel tolls are confirmed in your quote.",
      },
    ],
    nearby: ["queens-ny", "brooklyn-ny", "bronx-ny", "new-york-city-ny"],
  },
  {
    slug: "white-plains-ny",
    city: "White Plains",
    state: "NY",
    county: "Westchester County",
    title: "White Plains Car Service & Westchester Limo",
    description:
      "Chauffeured car and limo service in White Plains and Westchester County, NY. LaGuardia, JFK and Newark transfers and corporate rides. Call (929) 452-6031.",
    h1: "Limo Service in White Plains, NY",
    tagline:
      "Westchester business travel and airport runs, scheduled ahead from our New Jersey base.",
    intro: [
      "White Plains is the county seat of Westchester and a busy corporate hub, so many of our trips here are business rides: executives traveling between offices along the I-287 corridor and New Jersey, attorneys heading to the Westchester County Courthouse, and visitors staying downtown who need a reliable ride to the airport.",
      "From New Jersey, our chauffeurs usually take the Garden State Parkway and the Governor Mario M. Cuomo Bridge, then I-287 east into White Plains. The Hutchinson River and Bronx River parkways connect toward the airports, though vans follow commercial routes. All trips are pre-booked, with tolls confirmed ahead of time.",
    ],
    airports: [
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 30–50 min" },
      { code: "JFK", label: "JFK International", time: "About 45–75 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 45–75 min" },
    ],
    landmarks: [
      "Westchester County Center",
      "The Westchester mall",
      "White Plains Metro-North station",
      "Westchester County Courthouse",
      "Mamaroneck Avenue restaurant district",
      "I-287 corporate corridor",
    ],
    neighborhoods: [
      "Downtown White Plains",
      "Gedney Farms",
      "Battle Hill",
      "North White Plains",
      "Scarsdale border",
      "Harrison border",
    ],
    popular: [
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Executive rides between White Plains offices, New Jersey sites and Manhattan meetings.",
      },
      {
        label: "LaGuardia transfers",
        path: "/services/airport-transportation/lga",
        blurb:
          "Downtown hotel and office pickups for LaGuardia, scheduled around Hutch and I-678 traffic.",
      },
      {
        label: "Newark Airport rides",
        path: "/services/airport-transportation/ewr",
        blurb:
          "Westchester to EWR over the Cuomo Bridge, often a good option for United and international flights.",
      },
      {
        label: "Prom and formals",
        path: "/services/prom",
        blurb:
          "Group transportation for Westchester proms and school dances, with return times confirmed up front.",
      },
    ],
    faqs: [
      {
        q: "Do you cover the rest of Westchester County?",
        a: "Yes. Along with White Plains, we take pre-booked pickups in Scarsdale, Harrison, Rye, Tarrytown and other Westchester towns. Send your address when you request a quote.",
      },
      {
        q: "What is the best airport from White Plains?",
        a: "LaGuardia is usually the shortest drive. JFK and Newark are both practical depending on your airline, and we time the departure to the airport you are flying from.",
      },
      {
        q: "Can you drive me from White Plains to New Jersey?",
        a: "Yes. Westchester to New Jersey trips usually cross the Governor Mario M. Cuomo Bridge or the George Washington Bridge. Your quote lists the expected tolls before you confirm.",
      },
      {
        q: "Do you offer hourly service for business days?",
        a: "Yes. Hourly as-directed service keeps a chauffeur available for back-to-back meetings around White Plains, Purchase or Manhattan.",
      },
    ],
    nearby: ["bronx-ny", "new-york-city-ny", "queens-ny", "long-island-ny"],
  },
];
